import { DimensionType, Format, MetricType } from '../types/field';
import { customerFeedbackPack } from './customerFeedback';
import { nbfcForecastPack } from './nbfcForecast';
/**
 * DPD bucket boundaries, in days past due. A loan at exactly 90 DPD stays in the
 * 61-90 bucket; 91 DPD is the first day of the 91+ bucket.
 */
const DPD_BUCKET_1_30 = 30;
const DPD_BUCKET_31_60 = 60;
const DPD_BUCKET_61_90 = 90;
/** RBI-style NPA cut-off: an asset turns non-performing *above* 90 DPD. */
const NPA_DPD_THRESHOLD = 90;
/** RBI IRACP sub-buckets within Standard/SMA, and the Sub-standard cut-off. */
const SMA_1_DPD_MIN = 31;
const SMA_2_DPD_MIN = 61;
const SUB_STANDARD_DPD_MAX = 365;
/**
 * RBI standard provisioning rates by IRACP stage, base rate only. The real
 * unsecured-exposure +10% surcharge is NOT applied here: this data has no
 * collateral/security-type column, so applying it would mean guessing which
 * loans are unsecured. Omitted rather than assumed 0% (which would silently
 * understate risk) or assumed 10% (which would silently overstate it) —
 * neither guess is honest, so the surcharge is simply absent from this floor.
 */
const PROVISIONING_RATE_STANDARD = 0.004;
const PROVISIONING_RATE_SUB_STANDARD = 0.15;
/** Portfolio-at-Risk thresholds: PAR N covers loans at *or above* N DPD. */
const PAR_30_THRESHOLD = 30;
const PAR_60_THRESHOLD = 60;
const PAR_90_THRESHOLD = 90;
/** Any DPD above zero counts as delinquent. */
const DELINQUENT_DPD_THRESHOLD = 0;
/**
 * Delinquency-risk factor weights, in points of a 0-100 score. Carried over
 * unchanged from `DEFAULT_DELINQUENCY_CALIBRATION` in
 * `predictive/delinquencyRisk.ts`, which scored one loan at a time in Node.
 *
 * - dpdLevel 40: RBI asset classification is days-past-due driven, so current
 *   delinquency depth is the single strongest signal.
 * - collectionShortfall 25: this period's cash behaviour turns before DPD does.
 * - npaStatus 12: confirmation, deliberately small because it is mostly
 *   collinear with a high dpdLevel and would otherwise double-count.
 * - exposure 8: scales loss given default, not probability of default.
 *
 * The dpdTrajectory factor (15 points) is absent: it needs a prior DPD
 * observation, and a loan snapshot table has none. The Node version defined an
 * empty history as contributing exactly 0, so omitting it here reproduces that
 * case rather than reweighting the model. Scores therefore top out at 85, as
 * they did in Node for a first-seen loan.
 */
const RISK_WEIGHT_DPD_LEVEL = 40;
const RISK_WEIGHT_COLLECTION_SHORTFALL = 25;
const RISK_WEIGHT_NPA_STATUS = 12;
const RISK_WEIGHT_EXPOSURE = 8;
/** DPD at which the level factor reaches full severity: the NPA threshold. */
const RISK_DPD_SATURATION_DAYS = 90;
/**
 * Outstanding principal at which exposure reaches full severity. Roughly the
 * large-ticket end of this book; tune per portfolio.
 */
const RISK_EXPOSURE_SATURATION_AMOUNT = 1000000;
/** Lowest score entering each risk band, ascending within 0-100. */
const RISK_BAND_MEDIUM_THRESHOLD = 25;
const RISK_BAND_HIGH_THRESHOLD = 50;
const RISK_BAND_SEVERE_THRESHOLD = 75;
/** Bands, matching the DelinquencyRiskBand values the Node scorer returned. */
const RISK_BAND_LOW = 'LOW';
const RISK_BAND_MEDIUM = 'MEDIUM';
const RISK_BAND_HIGH = 'HIGH';
const RISK_BAND_SEVERE = 'SEVERE';
/**
 * Normalised 0-1 factor severities. LEAST/GREATEST clamp on all four supported
 * dialects. Composed as consts so the score, the band and each factor
 * contribution are all built from one definition.
 */
const DPD_SEVERITY_SQL = `LEAST(1, GREATEST(0, \${col.days_past_due} / ${RISK_DPD_SATURATION_DAYS}.0))`;
/** Nothing due means no collection signal this period, not a perfect payer. */
const SHORTFALL_SEVERITY_SQL = `COALESCE(LEAST(1, GREATEST(0, (\${col.installment_due} - \${col.installment_collected}) / NULLIF(\${col.installment_due}, 0))), 0)`;
const EXPOSURE_SEVERITY_SQL = `LEAST(1, GREATEST(0, \${col.outstanding_principal} / ${RISK_EXPOSURE_SATURATION_AMOUNT}.0))`;
/**
 * Derived from DPD, unlike the Node version which trusted a lender-supplied
 * `npa_status`. A restructured account classified NPA below 90 DPD is therefore
 * scored as Performing here; landing an `npa_status` column would fix that.
 */
const NPA_SEVERITY_SQL = `CASE WHEN \${col.days_past_due} > ${NPA_DPD_THRESHOLD} THEN 1 ELSE 0 END`;
const contributionSql = (weight, severitySql) => `${weight} * ${severitySql}`;
/** Weighted sum of every available factor, rounded as the Node scorer did. */
const RISK_SCORE_SQL = `ROUND(${[
    contributionSql(RISK_WEIGHT_DPD_LEVEL, DPD_SEVERITY_SQL),
    contributionSql(RISK_WEIGHT_COLLECTION_SHORTFALL, SHORTFALL_SEVERITY_SQL),
    contributionSql(RISK_WEIGHT_NPA_STATUS, NPA_SEVERITY_SQL),
    contributionSql(RISK_WEIGHT_EXPOSURE, EXPOSURE_SEVERITY_SQL),
].join('\n    + ')})`;
/**
 * Loan statuses meaning the account has left the live book.
 *
 * Deliberately a known-closed exclusion list rather than a known-open inclusion
 * list: an unrecognised status is then still counted. Over-reporting a portfolio
 * is visible and recoverable, whereas silently dropping loans from gross NPA
 * under-reports credit risk, which is the worse failure for a regulated lender.
 *
 * Only `closed` is evidenced by this repo (`seeds/loans.csv`, and the mart's
 * `where status = 'active'`). NEEDS A DOMAIN EXPERT: the other three are common
 * lender vocabulary, and whether a written-off account belongs in gross NPA is
 * an accounting policy call, not a naming one.
 */
const CLOSED_LOAN_STATUSES = [
    'closed',
    'written_off',
    'settled',
    'foreclosed',
];
/**
 * Status compared case- and separator-insensitively, so `CLOSED`,
 * `Written Off` and `written-off` all match the list above.
 */
const NORMALIZED_STATUS_SQL = `REPLACE(REPLACE(LOWER(TRIM(\${col.status})), ' ', '_'), '-', '_')`;
/**
 * The live book: every row whose status is not a known-closed one. A NULL status
 * is counted, for the same reason an unrecognised one is.
 *
 * This is the pack's population, applied as the Explore table's `sqlWhere`, so
 * it constrains every metric and dimension at once. Without it a closed account
 * reaches `gross_npa_ratio` — `LN-0013` in the fixture is closed at 365 DPD, and
 * would be counted as NPA.
 */
const ACTIVE_LOAN_POPULATION_SQL = `(\${col.status} IS NULL OR ${NORMALIZED_STATUS_SQL} NOT IN (${CLOSED_LOAN_STATUSES.map((status) => `'${status}'`).join(', ')}))`;
/**
 * Curated semantic model for an NBFC loan book, derived from the real columns in
 * `examples/lightbox-nbfc-loan-book` (`stg_loans` / `seeds/loans.csv`).
 *
 * The NPA and PAR definitions follow that project's `loan_portfolio` mart,
 * including its deliberate `> 90` NPA versus `>= 90` PAR asymmetry, and the pack
 * measures the same active-only population the mart selects.
 *
 * ponytail: the DPD bucketing is deliberately NOT identical to the mart. The
 * mart opens `Current` at `days_past_due = 0`, this pack at `<= 0`. A negative
 * DPD means paid ahead, which the mart drops into `1-30 DPD`, reporting a
 * prepaid account as delinquent. The pack is believed right and the mart wrong,
 * but the mart is the example project owner's dbt model and his call, so the
 * disagreement is recorded here rather than resolved. Reconcile with him before
 * relying on the two agreeing at the boundary.
 */
export const nbfcLoanBookPack = {
    id: 'nbfc_loan_book',
    name: 'NBFC loan book',
    description: 'Loan-level portfolio analytics with DPD bucketing, Portfolio at Risk and NPA ratios. Measures the live book: known-closed accounts are excluded.',
    populationFilter: ACTIVE_LOAN_POPULATION_SQL,
    /** One row per loan account. */
    grain: ['loan_id'],
    columns: [
        {
            key: 'customer_ref',
            synonyms: [
                'customer_ref',
                'customer_id',
                'borrower_id',
                'cust_id',
                'customer_number',
                'borrower_code',
                'party_id',
            ],
            type: DimensionType.STRING,
            required: false,
            // Optional, and the only column two packs share: it is what an
            // operator declares a cross-pack join on. A loan book that does not
            // carry one simply cannot be joined to feedback, which is honest.
            opaqueIdentifier: true,
            description: 'Borrower identifier, for joining to other sources about the same customer.',
        },
        {
            key: 'loan_id',
            synonyms: [
                'loan_id',
                'loan_account_id',
                'loan_account_number',
                'account_id',
                'account_number',
                'loan_no',
            ],
            type: DimensionType.STRING,
            required: true,
            description: 'Loan account identifier.',
        },
        {
            key: 'outstanding_principal',
            synonyms: [
                'outstanding_principal',
                'principal_outstanding',
                'outstanding_amount',
                'principal_balance',
                'outstanding_balance',
                'pos',
            ],
            type: DimensionType.NUMBER,
            required: true,
            description: 'Principal outstanding on the loan.',
        },
        {
            key: 'days_past_due',
            synonyms: [
                'days_past_due',
                'dpd',
                'days_overdue',
                'days_past_due_count',
                'overdue_days',
                'delinquency_days',
            ],
            type: DimensionType.NUMBER,
            required: true,
            description: 'Calendar days the loan is past due.',
        },
        {
            key: 'disbursed_amount',
            synonyms: [
                'disbursed_amount',
                'disbursement_amount',
                'sanctioned_amount',
                'loan_amount',
                'principal_disbursed',
            ],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Original sanctioned and disbursed principal.',
        },
        {
            key: 'disbursement_date',
            synonyms: [
                'disbursement_date',
                'disbursed_date',
                'disbursed_on',
                'loan_start_date',
                'origination_date',
            ],
            type: DimensionType.DATE,
            required: false,
            description: 'Date on which the loan was disbursed.',
        },
        {
            key: 'product_type',
            synonyms: [
                'product_type',
                'product',
                'product_name',
                'loan_type',
                'loan_product',
                'scheme',
            ],
            type: DimensionType.STRING,
            required: false,
            description: 'Loan product category.',
        },
        {
            key: 'branch_id',
            synonyms: [
                'branch_id',
                'branch_code',
                'branch',
                'branch_name',
                'servicing_branch',
            ],
            type: DimensionType.STRING,
            required: false,
            description: 'Servicing branch identifier.',
        },
        {
            key: 'region',
            synonyms: ['region', 'zone', 'state', 'geography', 'area'],
            type: DimensionType.STRING,
            required: false,
            description: 'Geographic region of the servicing branch.',
        },
        {
            key: 'status',
            synonyms: ['status', 'loan_status', 'account_status', 'loan_state'],
            type: DimensionType.STRING,
            required: false,
            description: 'Loan lifecycle status, e.g. active or closed.',
        },
        {
            key: 'installment_due',
            synonyms: [
                'installment_due',
                'instalment_due',
                'emi_due',
                'amount_due',
                'due_amount',
                'billed_amount',
            ],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Installment amount due for the reporting period.',
        },
        {
            key: 'installment_collected',
            synonyms: [
                'installment_collected',
                'instalment_collected',
                'emi_collected',
                'amount_collected',
                'collected_amount',
                'amount_paid',
            ],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Installment amount collected for the reporting period.',
        },
    ],
    dimensions: [
        {
            name: 'dpd_bucket',
            label: 'DPD bucket',
            description: 'Delinquency ageing bucket. 90 DPD remains in 61-90; 91 DPD enters 91+.',
            type: DimensionType.STRING,
            sql: `CASE
    WHEN \${col.days_past_due} <= 0 THEN 'Current'
    WHEN \${col.days_past_due} <= ${DPD_BUCKET_1_30} THEN '1-30 DPD'
    WHEN \${col.days_past_due} <= ${DPD_BUCKET_31_60} THEN '31-60 DPD'
    WHEN \${col.days_past_due} <= ${DPD_BUCKET_61_90} THEN '61-90 DPD'
    ELSE '91+ DPD'
END`,
        },
        {
            name: 'npa_status',
            label: 'NPA status',
            description: `NPA when days past due exceeds ${NPA_DPD_THRESHOLD}; otherwise Performing.`,
            type: DimensionType.STRING,
            sql: `CASE WHEN \${col.days_past_due} > ${NPA_DPD_THRESHOLD} THEN 'NPA' ELSE 'Performing' END`,
        },
        {
            name: 'disbursement_month',
            label: 'Disbursement month',
            description: 'Calendar month the loan was disbursed, for vintage/cohort curves.',
            type: DimensionType.DATE,
            // ponytail: DATE_TRUNC('MONTH', ...) is Postgres/Redshift/Snowflake/
            // DuckDB syntax, which covers every dialect this pack's shipped
            // example targets. BigQuery needs DATE_TRUNC(col, MONTH) (no
            // quotes) and ClickHouse needs toStartOfMonth(col) — pack
            // dimensions have no adapter-dispatch hook the way native Explore
            // date dimensions do, so widen this with an adapter switch if a
            // BigQuery/ClickHouse NBFC customer shows up.
            sql: `DATE_TRUNC('MONTH', \${col.disbursement_date})`,
        },
        {
            name: 'iracp_asset_stage',
            label: 'IRACP asset stage',
            description: `RBI income-recognition asset classification, staged purely from days past due: Standard (0), SMA-0 (1-${DPD_BUCKET_1_30}), SMA-1 (${SMA_1_DPD_MIN}-${DPD_BUCKET_31_60}), SMA-2 (${SMA_2_DPD_MIN}-${NPA_DPD_THRESHOLD}), Sub-standard (${NPA_DPD_THRESHOLD + 1}-${SUB_STANDARD_DPD_MAX}). Doubtful-1/2/3 are NOT modeled: RBI stages those by time-since-the-loan-first-became-NPA, which this table cannot compute — it has no first-NPA-date column or NPA history, only a point-in-time DPD snapshot. A loan past ${SUB_STANDARD_DPD_MAX} DPD is real Sub-standard-or-worse credit risk that this dimension under-states rather than mis-states; adding a first-NPA-date column (or an NPA history table) would unlock the Doubtful stages honestly.`,
            type: DimensionType.STRING,
            sql: `CASE
    WHEN \${col.days_past_due} <= 0 THEN 'Standard'
    WHEN \${col.days_past_due} < ${SMA_1_DPD_MIN} THEN 'SMA-0'
    WHEN \${col.days_past_due} < ${SMA_2_DPD_MIN} THEN 'SMA-1'
    WHEN \${col.days_past_due} <= ${NPA_DPD_THRESHOLD} THEN 'SMA-2'
    WHEN \${col.days_past_due} <= ${SUB_STANDARD_DPD_MAX} THEN 'Sub-standard'
    ELSE 'Sub-standard or worse (see description)'
END`,
        },
        {
            name: 'delinquency_risk_score',
            label: 'Delinquency risk score',
            description: 'Weighted delinquency risk, 0-100, from days past due, collection shortfall, NPA classification and exposure. Tops out at 85: the trajectory factor needs a prior DPD observation this table does not carry.',
            type: DimensionType.NUMBER,
            sql: RISK_SCORE_SQL,
        },
        {
            // ponytail: the score expression is inlined rather than referenced as
            // ${delinquency_risk_score}. A dimension referencing a dimension that
            // the generator dropped for a missing column would compile to a
            // dangling reference, and its cascade only covers metrics.
            name: 'delinquency_risk_band',
            label: 'Delinquency risk band',
            description: `Risk band: ${RISK_BAND_SEVERE} at ${RISK_BAND_SEVERE_THRESHOLD} and above, ${RISK_BAND_HIGH} at ${RISK_BAND_HIGH_THRESHOLD}, ${RISK_BAND_MEDIUM} at ${RISK_BAND_MEDIUM_THRESHOLD}, otherwise ${RISK_BAND_LOW}.`,
            type: DimensionType.STRING,
            sql: `CASE
    WHEN ${RISK_SCORE_SQL} >= ${RISK_BAND_SEVERE_THRESHOLD} THEN '${RISK_BAND_SEVERE}'
    WHEN ${RISK_SCORE_SQL} >= ${RISK_BAND_HIGH_THRESHOLD} THEN '${RISK_BAND_HIGH}'
    WHEN ${RISK_SCORE_SQL} >= ${RISK_BAND_MEDIUM_THRESHOLD} THEN '${RISK_BAND_MEDIUM}'
    ELSE '${RISK_BAND_LOW}'
END`,
        },
        {
            name: 'risk_contribution_dpd_level',
            label: 'Risk points from DPD level',
            description: `Points the loan's current delinquency depth contributes, of ${RISK_WEIGHT_DPD_LEVEL} at ${RISK_DPD_SATURATION_DAYS} DPD or more.`,
            type: DimensionType.NUMBER,
            sql: contributionSql(RISK_WEIGHT_DPD_LEVEL, DPD_SEVERITY_SQL),
        },
        {
            name: 'risk_contribution_collection_shortfall',
            label: 'Risk points from collection shortfall',
            description: `Points this period's uncollected installment contributes, of ${RISK_WEIGHT_COLLECTION_SHORTFALL}. Zero when nothing was due.`,
            type: DimensionType.NUMBER,
            sql: contributionSql(RISK_WEIGHT_COLLECTION_SHORTFALL, SHORTFALL_SEVERITY_SQL),
        },
        {
            name: 'risk_contribution_npa_status',
            label: 'Risk points from NPA status',
            description: `Points NPA classification contributes, of ${RISK_WEIGHT_NPA_STATUS}.`,
            type: DimensionType.NUMBER,
            sql: contributionSql(RISK_WEIGHT_NPA_STATUS, NPA_SEVERITY_SQL),
        },
        {
            name: 'risk_contribution_exposure',
            label: 'Risk points from exposure',
            description: `Points principal at stake contributes, of ${RISK_WEIGHT_EXPOSURE} at ${RISK_EXPOSURE_SATURATION_AMOUNT} outstanding or more.`,
            type: DimensionType.NUMBER,
            sql: contributionSql(RISK_WEIGHT_EXPOSURE, EXPOSURE_SEVERITY_SQL),
        },
    ],
    metrics: [
        {
            name: 'portfolio_outstanding',
            label: 'Portfolio outstanding',
            description: 'Total principal outstanding across loans.',
            type: MetricType.SUM,
            sql: '${col.outstanding_principal}',
            format: null,
            round: 2,
        },
        {
            name: 'loan_count',
            label: 'Loan count',
            description: 'Number of distinct loan accounts.',
            type: MetricType.COUNT_DISTINCT,
            sql: '${col.loan_id}',
            format: null,
            round: null,
        },
        {
            name: 'total_disbursed_amount',
            label: 'Disbursed amount',
            description: 'Total original principal disbursed.',
            type: MetricType.SUM,
            sql: '${col.disbursed_amount}',
            format: null,
            round: 2,
        },
        {
            name: 'average_ticket_size',
            label: 'Average ticket size',
            description: 'Mean disbursed principal per loan account.',
            type: MetricType.AVERAGE,
            sql: '${col.disbursed_amount}',
            format: null,
            round: 2,
        },
        {
            name: 'npa_outstanding',
            label: 'NPA outstanding',
            description: `Principal outstanding on loans above ${NPA_DPD_THRESHOLD} DPD.`,
            type: MetricType.SUM,
            sql: `CASE WHEN \${col.days_past_due} > ${NPA_DPD_THRESHOLD} THEN \${col.outstanding_principal} ELSE 0 END`,
            format: null,
            round: 2,
        },
        {
            name: 'npa_loan_count',
            label: 'NPA loans',
            description: `Loans above ${NPA_DPD_THRESHOLD} DPD.`,
            type: MetricType.SUM,
            sql: `CASE WHEN \${col.days_past_due} > ${NPA_DPD_THRESHOLD} THEN 1 ELSE 0 END`,
            format: null,
            round: null,
        },
        {
            name: 'gross_npa_ratio',
            label: 'Gross NPA ratio',
            description: 'NPA outstanding divided by total portfolio outstanding.',
            type: MetricType.NUMBER,
            sql: '${npa_outstanding} / NULLIF(${portfolio_outstanding}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'par_30_outstanding',
            label: 'PAR 30 outstanding',
            description: `Principal outstanding at ${PAR_30_THRESHOLD} or more DPD.`,
            type: MetricType.SUM,
            sql: `CASE WHEN \${col.days_past_due} >= ${PAR_30_THRESHOLD} THEN \${col.outstanding_principal} ELSE 0 END`,
            format: null,
            round: 2,
        },
        {
            name: 'par_30_ratio',
            label: 'PAR 30 ratio',
            description: 'Share of portfolio outstanding at 30 or more days past due.',
            type: MetricType.NUMBER,
            sql: '${par_30_outstanding} / NULLIF(${portfolio_outstanding}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'par_60_outstanding',
            label: 'PAR 60 outstanding',
            description: `Principal outstanding at ${PAR_60_THRESHOLD} or more DPD.`,
            type: MetricType.SUM,
            sql: `CASE WHEN \${col.days_past_due} >= ${PAR_60_THRESHOLD} THEN \${col.outstanding_principal} ELSE 0 END`,
            format: null,
            round: 2,
        },
        {
            name: 'par_60_ratio',
            label: 'PAR 60 ratio',
            description: 'Share of portfolio outstanding at 60 or more days past due.',
            type: MetricType.NUMBER,
            sql: '${par_60_outstanding} / NULLIF(${portfolio_outstanding}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'par_90_outstanding',
            label: 'PAR 90 outstanding',
            description: `Principal outstanding at ${PAR_90_THRESHOLD} or more DPD; includes loans at exactly ${PAR_90_THRESHOLD} DPD, which are not yet NPA.`,
            type: MetricType.SUM,
            sql: `CASE WHEN \${col.days_past_due} >= ${PAR_90_THRESHOLD} THEN \${col.outstanding_principal} ELSE 0 END`,
            format: null,
            round: 2,
        },
        {
            name: 'par_90_ratio',
            label: 'PAR 90 ratio',
            description: 'Share of portfolio outstanding at 90 or more days past due.',
            type: MetricType.NUMBER,
            sql: '${par_90_outstanding} / NULLIF(${portfolio_outstanding}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'delinquent_loan_count',
            label: 'Delinquent loans',
            description: 'Loans with any days past due.',
            type: MetricType.SUM,
            sql: `CASE WHEN \${col.days_past_due} > ${DELINQUENT_DPD_THRESHOLD} THEN 1 ELSE 0 END`,
            format: null,
            round: null,
        },
        {
            name: 'delinquency_rate',
            label: 'Delinquency rate',
            description: 'Share of loan accounts with any days past due.',
            type: MetricType.NUMBER,
            sql: '${delinquent_loan_count} / NULLIF(${loan_count}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'total_installment_due',
            label: 'Installment due',
            description: 'Total installment amount due for the period.',
            type: MetricType.SUM,
            sql: '${col.installment_due}',
            format: null,
            round: 2,
        },
        {
            name: 'total_installment_collected',
            label: 'Installment collected',
            description: 'Total installment amount collected for the period.',
            type: MetricType.SUM,
            sql: '${col.installment_collected}',
            format: null,
            round: 2,
        },
        {
            name: 'collection_efficiency',
            label: 'Collection efficiency',
            description: 'Installment collected divided by installment due.',
            type: MetricType.NUMBER,
            sql: '${total_installment_collected} / NULLIF(${total_installment_due}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'average_delinquency_risk_score',
            label: 'Average delinquency risk score',
            description: 'Mean delinquency risk score across loans, 0-100. Unweighted by exposure — pair with at-risk outstanding to size the exposure.',
            type: MetricType.AVERAGE,
            sql: RISK_SCORE_SQL,
            format: null,
            round: 1,
        },
        {
            name: 'at_risk_loan_count',
            label: 'At-risk loans',
            description: `Loans scoring ${RISK_BAND_HIGH_THRESHOLD} or above, i.e. band ${RISK_BAND_HIGH} or ${RISK_BAND_SEVERE}.`,
            type: MetricType.SUM,
            sql: `CASE WHEN ${RISK_SCORE_SQL} >= ${RISK_BAND_HIGH_THRESHOLD} THEN 1 ELSE 0 END`,
            format: null,
            round: null,
        },
        {
            name: 'severe_risk_loan_count',
            label: 'Severe-risk loans',
            description: `Loans scoring ${RISK_BAND_SEVERE_THRESHOLD} or above.`,
            type: MetricType.SUM,
            sql: `CASE WHEN ${RISK_SCORE_SQL} >= ${RISK_BAND_SEVERE_THRESHOLD} THEN 1 ELSE 0 END`,
            format: null,
            round: null,
        },
        {
            name: 'at_risk_outstanding',
            label: 'At-risk outstanding',
            description: `Principal outstanding on loans scoring ${RISK_BAND_HIGH_THRESHOLD} or above.`,
            type: MetricType.SUM,
            sql: `CASE WHEN ${RISK_SCORE_SQL} >= ${RISK_BAND_HIGH_THRESHOLD} THEN \${col.outstanding_principal} ELSE 0 END`,
            format: null,
            round: 2,
        },
        {
            name: 'at_risk_outstanding_ratio',
            label: 'At-risk outstanding ratio',
            description: 'Share of portfolio outstanding on loans scoring in the high or severe band.',
            type: MetricType.NUMBER,
            sql: '${at_risk_outstanding} / NULLIF(${portfolio_outstanding}, 0)',
            format: Format.PERCENT,
            round: 2,
        },
        {
            name: 'provisioning_estimate',
            label: 'Provisioning estimate (floor)',
            description: 'Standard-rate RBI provisioning (Standard/SMA 0.40%, Sub-standard 15%) applied to outstanding principal by IRACP stage. This is a FLOOR estimate, not the true IndAS/RBI-compliant provisioning figure: it omits the +10% unsecured-exposure surcharge (no collateral/security-type column exists in this data) and stops staging at Sub-standard (no Doubtful-1/2/3, see iracp_asset_stage). The real provisioning requirement is at or above this number, never below it.',
            type: MetricType.SUM,
            sql: `CASE
    WHEN \${col.days_past_due} > ${NPA_DPD_THRESHOLD} THEN \${col.outstanding_principal} * ${PROVISIONING_RATE_SUB_STANDARD}
    ELSE \${col.outstanding_principal} * ${PROVISIONING_RATE_STANDARD}
END`,
            format: null,
            round: 2,
        },
    ],
};
/** Every pack shipped with the platform. Adding a pack is adding data here. */
export const domainPacks = [
    nbfcLoanBookPack,
    customerFeedbackPack,
    nbfcForecastPack,
];
//# sourceMappingURL=nbfcLoanBook.js.map